import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Link2, ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function DealerLinkRequest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [form, setForm] = useState({
    dealer_url: "", year: "", make: "", model: "", trim: "", color: "",
    msrp: "", vin: "", stock_number: "", dealer_location: "", option_packages: "",
    delivery_zip: user?.address_zip || "", buyer_phone: user?.phone || "", notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isValidUrl = (u) => { try { new URL(u); return true; } catch { return false; } };

  const goExtract = async () => {
    if (!isValidUrl(form.dealer_url)) { toast.error("Please paste a valid dealer inventory URL"); return; }
    setStep(1);
    setExtracting(true);
    try {
      const { data } = await api.post("/extract-link", { dealer_url: form.dealer_url });
      setForm((f) => ({
        ...f,
        year: data.year || f.year,
        make: data.make || f.make,
        model: data.model || f.model,
        trim: data.trim || f.trim,
        color: data.color || f.color,
        msrp: data.msrp != null ? String(data.msrp) : f.msrp,
        vin: data.vin || f.vin,
        stock_number: data.stock_number || f.stock_number,
        dealer_location: data.dealer_location || f.dealer_location,
      }));
      const found = data.fields_found || [];
      if (found.length) toast.success(`Prefilled ${found.length} detail${found.length !== 1 ? "s" : ""} from the listing — please review`);
      else toast.message("We couldn't read that listing automatically — please fill in the details below");
    } catch {
      toast.message("We couldn't read that listing automatically — please fill in the details below");
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    if (!form.year || !form.make || !form.model) { toast.error("Year, make and model are required"); return; }
    if (!form.delivery_zip) { toast.error("Delivery ZIP is required"); return; }
    try {
      const { data } = await api.post("/requests", {
        source: "dealer_link",
        dealer_url: form.dealer_url,
        year: parseInt(form.year) || null,
        make: form.make, model: form.model, trim: form.trim, color: form.color,
        msrp: form.msrp ? parseFloat(form.msrp) : null,
        vin: form.vin, stock_number: form.stock_number, dealer_location: form.dealer_location,
        option_packages: form.option_packages ? form.option_packages.split(",").map((s) => s.trim()).filter(Boolean) : [],
        delivery_zip: form.delivery_zip, buyer_phone: form.buyer_phone, notes: form.notes,
      });
      toast.success("Request submitted for broker verification");
      navigate(`/requests/${data.id}`);
    } catch (e) {
      toast.error("Could not submit request");
    }
  };

  const missing = !form.year || !form.make || !form.model || !form.color || !form.msrp || !form.vin;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <div className="mb-8 flex items-center gap-2">
          {["Dealer link", "Confirm details", "Delivery"].map((s, i) => (
            <div key={i} className="flex flex-1 items-center gap-2">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${step >= i ? "bg-slate-950 text-white" : "bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400"}`}>{i + 1}</div>
              <span className={`text-xs font-semibold ${step >= i ? "text-slate-900 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"}`}>{s}</span>
              {i < 2 && <div className={`h-0.5 flex-1 ${step > i ? "bg-slate-950" : "bg-slate-200 dark:bg-slate-800"}`} />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="ab-fade-up rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-rose-50 text-rose-600"><Link2 className="h-5 w-5" strokeWidth={2.5} /></div>
            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-950 dark:text-slate-50">Paste a dealer inventory link</h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Found a brand-new vehicle at a California dealer? Drop the inventory URL and we'll read the listing and prefill what we can — you review and confirm every detail before submitting. Our brokers then verify availability manually with the dealer.</p>
            <Label className="mt-6 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Dealer inventory URL</Label>
            <Input data-testid="dealer-url-input" value={form.dealer_url} onChange={(e) => set("dealer_url", e.target.value)} placeholder="https://dealer.example.com/inventory/..." className="mt-1.5 border-2 py-6 text-base" />
            <Button data-testid="extract-btn" onClick={goExtract} className="mt-6 w-full rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800">
              Continue <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}

        {step === 1 && (
          <div className="ab-fade-up rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-950 dark:text-slate-50">Confirm vehicle details</h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">We prefilled what we could read from the listing. Review and correct anything — our team confirms and completes missing details during verification.</p>
            {extracting ? (
              <div className="mt-6 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                <p className="text-center text-sm text-slate-400 dark:text-slate-500">Reading the dealer listing…</p>
              </div>
            ) : (
              <>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {[["year", "Year *", "2025"], ["make", "Make *", "Toyota"], ["model", "Model *", "RAV4"], ["trim", "Trim", "XLE Premium"],
                    ["color", "Color", "Blueprint"], ["msrp", "MSRP ($)", "38999"], ["vin", "VIN", "17-char VIN"], ["stock_number", "Stock #", "T12345"],
                    ["dealer_location", "Dealer location", "Los Angeles, CA"]].map(([k, label, ph]) => (
                    <div key={k}>
                      <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</Label>
                      <Input data-testid={`field-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="mt-1.5" />
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Option packages (comma separated)</Label>
                  <Input data-testid="field-options" value={form.option_packages} onChange={(e) => set("option_packages", e.target.value)} placeholder="Weather package, Tow hitch" className="mt-1.5" />
                </div>
                {missing && (
                  <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                    Some details are incomplete. That's okay — our brokers will follow up on anything missing before your quote.
                  </div>
                )}
                <div className="mt-6 flex gap-3">
                  <Button variant="outline" onClick={() => setStep(0)} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="back-step-btn"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
                  <Button data-testid="to-delivery-btn" onClick={() => setStep(2)} className="flex-1 rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800">Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="ab-fade-up rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-950 dark:text-slate-50">Delivery preference</h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Where should we plan to deliver, if you choose delivery over dealer pickup?</p>
            <div className="mt-6">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Delivery ZIP code *</Label>
              <Input data-testid="field-delivery_zip" value={form.delivery_zip} onChange={(e) => set("delivery_zip", e.target.value)} placeholder="90001" className="mt-1.5" />
            </div>
            <div className="mt-4">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Mobile number (for SMS / WhatsApp status alerts)</Label>
              <Input data-testid="field-buyer_phone" value={form.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} placeholder="+1 415 555 1234" className="mt-1.5" />
            </div>
            <div className="mt-4">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Anything else we should know?</Label>
              <Textarea data-testid="field-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Timing, must-have options, questions…" className="mt-1.5" rows={3} />
            </div>
            <div className="mt-6 rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300"><ShieldCheck className="h-4 w-4 text-rose-600" /> What happens next</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> A broker manually verifies availability with the dealer</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> You receive a confirmed quote path</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> Track every step from your dashboard</li>
              </ul>
            </div>
            <div className="mt-6 flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="back-step2-btn"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
              <Button data-testid="submit-request-btn" onClick={submit} className="flex-1 rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800">Submit request</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
